import {
  Pencil01Icon,
  PlusIcon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DeleteModal } from '@/components/Delete';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@/components/Select/Select';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import useToast from '@/components/Toast/useToast';
import { Button, IconButton, Text, Tooltip } from '@/design-system';

import { DEFAULT_CONFIG_ID } from '../../constants';
import { useValidateForm } from '../../hooks/useValidateForm';
import { InsightsJobConfigWithId, InsightsJobFormData } from '../../types';
import { getDiffKeys } from '../../utils/formatUtils';
import { SaveAsDialog } from './SaveAsDialog';
import { SaveDialog } from './SaveDialog';
import { BASE_INSIGHTS_JOB_CONFIGS_URL } from './hooks/useInsightsJobConfigs';

const SavedConfigItem = ({ config }: { config: InsightsJobConfigWithId }) => {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 py-px">
      <TextOverflowTooltip textVariant="sm" className="font-semibold">
        {config.name}
      </TextOverflowTooltip>
    </div>
  );
};

const SelectableSavedConfigItem = ({
  config,
  isSelected,
  onSelectConfig,
  onEdit,
  onDelete,
}: {
  config: InsightsJobConfigWithId;
  isSelected: boolean;
  onSelectConfig: (config: InsightsJobConfigWithId) => void;
  onEdit: (config: InsightsJobConfigWithId) => void;
  onDelete: (config: InsightsJobConfigWithId) => void;
}) => {
  return (
    <Button
      variant="plain"
      color="secondary"
      size="md"
      className="mx-1 px-2 py-2 outline-none hover:bg-secondary data-[selected=true]:bg-tertiary"
      data-selected={isSelected}
      onClick={() => onSelectConfig(config)}
    >
      <div className="group flex w-full items-center justify-between">
        <div className="flex flex-col gap-2 overflow-hidden">
          <SavedConfigItem config={config} />
          {config.description && (
            <TextOverflowTooltip textVariant="xs">
              {config.description}
            </TextOverflowTooltip>
          )}
        </div>
        {!config.prebuilt && (
          <div className="flex flex-shrink-0 items-center opacity-0 group-hover:opacity-100">
            <IconButton
              variant="plain"
              color="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onEdit(config);
              }}
              icon={Pencil01Icon}
              label="Edit config name"
              className="bg-hover-primary hover:bg-brand-secondary"
            />
            <IconButton
              variant="plain"
              color="error"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(config);
              }}
              icon={Trash01Icon}
              label="Delete config"
            />
          </div>
        )}
      </div>
    </Button>
  );
};

const SelectSectionHeader = ({ title }: { title: string }) => {
  return (
    <div className="-mx-1 my-1 bg-tertiary px-4">
      <Text
        variant="sm"
        weight="medium"
        className="uppercase"
        color="quaternary"
      >
        {title}
      </Text>
    </div>
  );
};

const SelectSection = ({
  title,
  configs,
  selectedConfigId,
  onSelectConfig,
  onEditConfig,
  onDeleteConfig,
}: {
  title: string;
  configs: InsightsJobConfigWithId[];
  selectedConfigId: string | null;
  onSelectConfig: (config: InsightsJobConfigWithId) => void;
  onEditConfig: (config: InsightsJobConfigWithId) => void;
  onDeleteConfig: (config: InsightsJobConfigWithId) => void;
}) => {
  return (
    <div className="flex flex-col">
      <SelectSectionHeader title={title} />
      {configs.map((config) => (
        <SelectableSavedConfigItem
          key={config.id}
          config={config}
          isSelected={config.id === selectedConfigId}
          onSelectConfig={onSelectConfig}
          onEdit={onEditConfig}
          onDelete={onDeleteConfig}
        />
      )) ?? null}
    </div>
  );
};

const DropdownContent = ({
  configs,
  selectedConfigId,
  onSelectConfig,
  onEditConfig,
  onDeleteConfig,
  resetForm,
}: {
  configs: InsightsJobConfigWithId[];
  selectedConfigId: string | null;
  onSelectConfig: (config: InsightsJobConfigWithId) => void;
  onEditConfig: (config: InsightsJobConfigWithId) => void;
  onDeleteConfig: (config: InsightsJobConfigWithId) => void;
  resetForm: () => void;
}) => {
  const [prebuiltConfigs, customConfigs] = useMemo(() => {
    return configs.reduce(
      (acc, config) => {
        if (config.prebuilt) {
          acc[0].push(config);
        } else {
          acc[1].push(config);
        }
        return acc;
      },
      [[], []] as [InsightsJobConfigWithId[], InsightsJobConfigWithId[]]
    );
  }, [configs]);

  return (
    <>
      <div className="mx-3 my-2 flex items-center justify-between">
        <Text variant="h3" weight="semibold">
          Configurations
        </Text>
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          onClick={resetForm}
          leftDecorator={PlusIcon}
        >
          New
        </Button>
      </div>
      <SelectSection
        title="Pre-built"
        configs={prebuiltConfigs}
        selectedConfigId={selectedConfigId}
        onSelectConfig={onSelectConfig}
        onEditConfig={onEditConfig}
        onDeleteConfig={onDeleteConfig}
      />
      {customConfigs.length > 0 && (
        <SelectSection
          title="Custom"
          configs={customConfigs}
          selectedConfigId={selectedConfigId}
          onSelectConfig={onSelectConfig}
          onEditConfig={onEditConfig}
          onDeleteConfig={onDeleteConfig}
        />
      )}
    </>
  );
};

const SavedConfigSelector = ({
  configs,
  setSelectedConfig,
  selectedConfigId,
  resetForm,
}: {
  configs: InsightsJobConfigWithId[];
  setSelectedConfig: (id: string) => void;
  selectedConfigId: string | null;
  resetForm: () => void;
}) => {
  const { sessionId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const selectedConfig = configs.find(
    (config) => config.id === selectedConfigId
  );
  const [editingConfig, setEditingConfig] =
    useState<InsightsJobConfigWithId | null>(null);
  const [deletingConfig, setDeletingConfig] =
    useState<InsightsJobConfigWithId | null>(null);

  const onEdit = (config: InsightsJobConfigWithId) => {
    setEditingConfig(config);
    setIsOpen(false);
  };

  const onDelete = (config: InsightsJobConfigWithId) => {
    setDeletingConfig(config);
    setIsOpen(false);
  };

  return (
    <>
      <Select
        open={isOpen}
        onOpenChange={setIsOpen}
        value={selectedConfigId || undefined}
      >
        <SelectTrigger
          className="w-full min-w-0 rounded-sm px-2 py-1.5"
          onClick={() => setIsOpen(isOpen)}
        >
          <div className="flex min-w-0 items-center gap-2">
            {selectedConfig ? (
              <SavedConfigItem config={selectedConfig} />
            ) : (
              <Text variant="sm" className="whitespace-nowrap">
                Select config
              </Text>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="max-w-[300px]">
          <DropdownContent
            configs={configs}
            selectedConfigId={selectedConfigId}
            onSelectConfig={(config) => {
              setSelectedConfig(config.id);
              setIsOpen(false);
            }}
            onEditConfig={onEdit}
            onDeleteConfig={onDelete}
            resetForm={() => {
              resetForm();
              setIsOpen(false);
            }}
          />
        </SelectContent>
      </Select>
      {editingConfig && (
        <SaveDialog
          isOpen={true}
          onOpenChange={(open) => !open && setEditingConfig(null)}
          diffKeys={[]}
          selectedConfig={editingConfig}
        />
      )}
      {deletingConfig && sessionId && (
        <DeleteModal
          isOpen={true}
          doClose={() => {
            setDeletingConfig(null);
            setIsOpen(true);
          }}
          endpoint={BASE_INSIGHTS_JOB_CONFIGS_URL(sessionId)}
          id={deletingConfig.id}
          name={deletingConfig.name}
          onSuccess={() => {
            if (deletingConfig.id === selectedConfigId) {
              const defaultConfig = configs.find(
                (config) => config.id === DEFAULT_CONFIG_ID
              );
              if (defaultConfig) {
                setSelectedConfig(defaultConfig.id);
              }
            }
            setDeletingConfig(null);
          }}
        />
      )}
    </>
  );
};

export const SavedConfigs = ({
  formData,
  selectedConfigId,
  configs,
  setSelectedConfig,
  setErrors,
  resetForm,
}: {
  formData: InsightsJobFormData;
  selectedConfigId: string | null;
  configs: InsightsJobConfigWithId[];
  setSelectedConfig: ({
    id,
    skipContentUpdate,
  }: {
    id: string;
    skipContentUpdate?: boolean;
  }) => void;
  setErrors: (
    errors: Partial<Record<keyof InsightsJobFormData, string>>
  ) => void;
  resetForm: () => void;
}) => {
  const { createToast } = useToast();

  const selectedConfig = useMemo(
    () => configs.find((config) => config.id === selectedConfigId),
    [configs, selectedConfigId]
  );

  const { validateForm } = useValidateForm({
    formData,
    setErrors,
  });

  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);

  // List different fields between old and new config
  const diffKeys = useMemo(
    () => getDiffKeys(selectedConfig?.config ?? {}, formData),
    [selectedConfig?.config, formData]
  );

  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="min-w-0 flex-1">
        <SavedConfigSelector
          configs={configs}
          setSelectedConfig={(id) => setSelectedConfig({ id })}
          selectedConfigId={selectedConfigId}
          resetForm={resetForm}
        />
      </div>
      <div className="flex-shrink-0">
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          onClick={() => {
            if (!validateForm()) {
              createToast({
                title: 'Config is invalid',
                description: 'Please fix the errors before saving.',
                type: 'error',
              });
              return;
            }
            setIsSaveAsOpen(true);
          }}
        >
          Save as
        </Button>
      </div>
      <SaveAsDialog
        isOpen={isSaveAsOpen}
        onOpenChange={setIsSaveAsOpen}
        formData={formData}
        setErrors={setErrors}
        setSelectedConfig={(id) =>
          setSelectedConfig({ id: id, skipContentUpdate: true })
        }
      />
      {selectedConfigId && (
        <div className="flex-shrink-0">
          <Tooltip
            title={
              diffKeys.length > 0
                ? 'Reset to saved config settings'
                : 'No changes to reset'
            }
          >
            <Button
              variant="underlined"
              color="secondary"
              size="sm"
              onClick={() => {
                setSelectedConfig({ id: selectedConfigId });
              }}
              disabled={diffKeys.length === 0}
            >
              Reset
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
